import React, { useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  CAB_DECK_HTML_MAX_CHARS,
  CAB_DECK_QUILL_FORMATS,
  CAB_DECK_QUILL_MODULES,
  normalizeCabDeckRichHtml,
  registerCabDeckQuillSize,
} from '../cabDeckRichText';

registerCabDeckQuillSize();

const COMMIT_MS = 600;

/**
 * Quill não controlado. Commit com debounce (não no blur da toolbar):
 * clicar em negrito/cor A tirava o foco, perdia a seleção e pintava o bloco inteiro.
 */
export default function CabDeckQuillEditor({
  elementKey,
  content,
  disabled,
  onCommit,
}: {
  elementKey: string | number;
  content: string;
  disabled?: boolean;
  onCommit: (html: string, key: string | number) => void;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const onCommitRef = useRef(onCommit);
  const lastSentRef = useRef(String(content || '').slice(0, CAB_DECK_HTML_MAX_CHARS));
  const pendingRef = useRef<string | null>(null);
  const keyRef = useRef(elementKey);
  const timerRef = useRef<number | null>(null);
  const blurTimerRef = useRef<number | null>(null);
  onCommitRef.current = onCommit;

  const clearCommitTimer = () => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const clearBlurTimer = () => {
    if (blurTimerRef.current != null) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  };

  const flushIfNeeded = (key: string | number) => {
    clearCommitTimer();
    const html = pendingRef.current;
    pendingRef.current = null;
    if (html == null) return;
    if (html === lastSentRef.current) return;
    lastSentRef.current = html;
    onCommitRef.current(html, key);
  };

  const scheduleCommit = (key: string | number) => {
    clearCommitTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      flushIfNeeded(key);
    }, COMMIT_MS);
  };

  useEffect(() => {
    if (keyRef.current === elementKey) return;
    flushIfNeeded(keyRef.current);
    keyRef.current = elementKey;
    lastSentRef.current = String(content || '').slice(0, CAB_DECK_HTML_MAX_CHARS);
    pendingRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementKey]);

  useEffect(() => {
    return () => {
      flushIfNeeded(keyRef.current);
      clearBlurTimer();
    };
  }, []);

  useEffect(() => {
    const onFlushAll = () => flushIfNeeded(keyRef.current);
    window.addEventListener('cab-deck-quill-flush', onFlushAll);
    return () => window.removeEventListener('cab-deck-quill-flush', onFlushAll);
  }, []);

  if (disabled) {
    return (
      <div
        className="border rounded p-2 small bg-light cab-deck-el-rich"
        dangerouslySetInnerHTML={{
          __html: normalizeCabDeckRichHtml(content) || '<p></p>',
        }}
      />
    );
  }

  return (
    <div className="cab-deck-quill" ref={rootRef}>
      <ReactQuill
        key={String(elementKey)}
        theme="snow"
        defaultValue={String(content || '').slice(0, CAB_DECK_HTML_MAX_CHARS)}
        onChange={(html, _delta, source) => {
          if (source !== 'user') return;
          pendingRef.current = String(html || '').slice(0, CAB_DECK_HTML_MAX_CHARS);
          scheduleCommit(keyRef.current);
        }}
        onBlur={() => {
          /* Toolbar do Quill tira o foco do editor: esperar e só commit se o foco saiu do bloco. */
          clearBlurTimer();
          blurTimerRef.current = window.setTimeout(() => {
            blurTimerRef.current = null;
            const active = document.activeElement;
            if (rootRef.current && active && rootRef.current.contains(active)) return;
            flushIfNeeded(keyRef.current);
          }, 120);
        }}
        modules={CAB_DECK_QUILL_MODULES}
        formats={CAB_DECK_QUILL_FORMATS}
        placeholder="Selecione um trecho e use a barra: negrito, tamanho, cor…"
      />
    </div>
  );
}
